import React from "react"
import {
  ErrorObject,
  useErrorBoundary,
} from "@myvp/shared/src/hooks/use-error-boundary"
import { useIntl } from "react-intl"
import { HttpCode } from "@myvp/shared/src/types"
import { useLocation, useMatches, useParams } from "react-router"
import { useQuery } from "@tanstack/react-query"
import { getVaultDnsQuery } from "src/auth/auth.model"
import studyRouteNames from "src/pages/study/study-route-names"
import { eproModuleRouteNames } from "src/pages/epro-module/epro-module.route-names"
import { getRegion } from "@myvp/shared/src/functions/get-region"
import { setSponsorsBookmark } from "src/functions/manage-sponsors-bookmark"
import { libraryManagerRouteNames } from "src/pages/library-manager/library-manager.route-names"
import MessageDialog from "@myvp/shared/src/components/message-dialog"
import { RouteRedirectOnStudyLockedHandle } from "src/pages/epro-module/create-epro-module-routes"

const region = getRegion()
const Unauthorized = () => {
  const { vaultId, globalSponsorStudyId } = useParams<
    "vaultId" | "globalSponsorStudyId"
  >()
  const location = useLocation()

  let context: "studio" | "studyhome" | "library" | null = null

  if (vaultId && globalSponsorStudyId) {
    if (
      location.pathname.startsWith(
        studyRouteNames.study({ vaultId, globalSponsorStudyId })
      )
    ) {
      context = "studyhome"
    } else if (
      location.pathname.startsWith(
        eproModuleRouteNames.studio({
          vaultId: vaultId as string,
          globalSponsorStudyId: globalSponsorStudyId as string,
        })
      )
    ) {
      context = "studio"
    }
  } else if (
    vaultId &&
    location.pathname.startsWith(
      libraryManagerRouteNames.standaloneLibrary({ vaultId })
    )
  ) {
    context = "library"
  }
  const query = useQuery({
    ...getVaultDnsQuery({ vaultId: vaultId as string }),
    enabled:
      context === "library" ? !!vaultId : !!vaultId && !!globalSponsorStudyId,
  })
  React.useEffect(() => {
    // If this isn't successful then it goes to the root fallback case
    if (query.isSuccess) {
      // Clear all sessionStorage which may include other bookmark
      sessionStorage.clear()
      if (query.data?.vaultDns && context) {
        const bookmark = new URL(window.location.toString())
        if (globalSponsorStudyId) {
          // Put globalSponsorStudyId in search params to easily compare
          bookmark.searchParams.set(
            "globalSponsorStudyId",
            globalSponsorStudyId
          )
        }
        setSponsorsBookmark(bookmark)
        const url = new URL(query.data.vaultDns)
        url.pathname = "/ui"
        const searchParams = new URLSearchParams()
        searchParams.set("sreg", region)
        if (globalSponsorStudyId) {
          searchParams.set("studyId", globalSponsorStudyId)
        }
        searchParams.set("context", context)
        if (process.env.NODE_ENV === "development") {
          // MyVeeva auth side will look at this query and redirect back to local
          searchParams.set("localProxy", "true")
        }
        url.search = searchParams.toString()
        window.location.assign(url.toString())
      } else {
        // vaultDns being null means 404, so redirect to login
        window.location.assign(process.env.REACT_APP_vault_login_url as string)
      }
    } else if (!vaultId && !globalSponsorStudyId) {
      // No vaultId or globalSponsorStudyId means you went to the error page itself with code=401, so redirect back to login
      window.location.assign(process.env.REACT_APP_vault_login_url as string)
    }
  }, [
    context,
    globalSponsorStudyId,
    query.data?.vaultDns,
    query.isSuccess,
    vaultId,
  ])
  return <React.Fragment />
}
export const useRootErrorBoundaryConfig = (): Record<string, ErrorObject> => {
  const { formatMessage } = useIntl()
  const location = useLocation()
  const params = useParams()
  const matches = useMatches()
  const redirectOnStudyLockedPath = (
    matches.find(
      (match) =>
        (match.handle as RouteRedirectOnStudyLockedHandle)
          ?.redirectOnStudyLocked
    )?.handle as RouteRedirectOnStudyLockedHandle
  )?.redirectOnStudyLocked

  return {
    [HttpCode.BadRequest]: {
      title: formatMessage({ id: "error.fallbackTitle" }),
      message: formatMessage({ id: "error.fallbackMessage" }),
    },
    [HttpCode.Unauthorized]: {
      render: () => <Unauthorized />,
      title: undefined,
      message: undefined,
    },
    [HttpCode.Forbidden]: {
      title: formatMessage({ id: "error.403Title" }),
      message: formatMessage({ id: "error.403Message" }),
    },
    [HttpCode.ServiceUnavailable]: {
      title: formatMessage({ id: "error.fallbackTitle" }),
      message: formatMessage({ id: "error.503Message" }),
      onConfirm: () => window.location.reload(),
      onConfirmText: formatMessage({ id: "error.refresh" }),
    },
    STUDY_LOCKED: {
      title: undefined,
      message: undefined,
      render: () => {
        return (
          <MessageDialog
            visible={true}
            variant="alert"
            title={formatMessage({ id: "error.studyLockedTitle" })}
            message={formatMessage({ id: "error.studyLockedMessage" })}
            onCloseText={""}
            onConfirm={() => {
              if (redirectOnStudyLockedPath) {
                const url = redirectOnStudyLockedPath({
                  ...(params as any),
                })
                window.location.assign(`${url}${location.search}`)
              } else {
                window.location.reload()
              }
            }}
            onConfirmText={formatMessage({
              id: "eproModule.ok",
            })}
          />
        )
      },
    },
    EXTERNAL_INTEGRATION_INACTIVE: {
      title: formatMessage({ id: "eproModule.errors.412-externalApiTitle" }),
      message: formatMessage({
        id: "eproModule.errors.412-externalApiMessage",
      }),
      onConfirm: () => {
        window.location.reload()
      },
      onConfirmText: formatMessage({ id: "error.refresh" }),
    },
    fallback: {
      title: formatMessage({ id: "error.fallbackTitle" }),
      message: formatMessage({ id: "error.fallbackMessage" }),
      onConfirm: () => window.location.reload(),
      onConfirmText: formatMessage({ id: "error.refresh" }),
    },
  }
}

export default function RootErrorBoundary() {
  const errors = useErrorBoundary(useRootErrorBoundaryConfig(), {
    readKey: true,
  })
  if (!errors) {
    return null
  }
  if (errors.render) {
    return <React.Fragment>{errors.render()}</React.Fragment>
  }

  return <MessageDialog {...errors} onCloseText={false} variant="alert" />
}
